import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NovalaVerifyModal from "../../components/NovalaVerifyModal";
import { getToken } from '../../utils/auth';
import type { ApiError } from './types';

const API = "https://api.getnovala.com/api/v1";

export default function Verify() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sendError, setSendError] = useState<string>("");
  const sentRef = useRef<boolean>(false);

  const email: string = localStorage.getItem("user_email") || localStorage.getItem("email") || "";
  const phone: string = localStorage.getItem("signup_phone") || "";

  const sendCode = async (): Promise<void> => {
    const token = getToken();
    if (!token) { navigate("/login"); return; }
    setSendError("");
    try {
      const res = await fetch(API + "/auth/send-verification-code", {
        method: "POST",
        headers: { "Authorization": "Bearer " + token, "Content-Type": "application/json" }
      });
      if (!res.ok) {
        const err = await res.json().catch<ApiError>(() => ({ detail: t("checkout.verify.couldNotSend") }));
        setSendError(err.detail || t("checkout.verify.couldNotSend"));
      }
    } catch (e) {
      setSendError(t("checkout.verify.networkError"));
    }
  };

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCodeSubmit = async (code: string): Promise<boolean> => {
    const token = getToken();
    if (!token) return false;
    try {
      const res = await fetch(API + "/auth/verify-code", {
        method: "POST",
        headers: { "Authorization": "Bearer " + token, "Content-Type": "application/json" },
        body: JSON.stringify({ code: code })
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  };

  const handleVerified = (): void => {
    const params = new URLSearchParams();
    params.set("fromCheckout", "true");
    const plan = searchParams.get("plan");
    const billing = searchParams.get("billing");
    const payroll = searchParams.get("payroll");
    if (plan) params.set("plan", plan);
    if (billing) params.set("billing", billing);
    if (payroll) params.set("payroll", payroll);
    navigate("/billing?" + params.toString());
  };

  const handleResend = (): void => { sendCode(); };
  const handleClose = (): void => { navigate("/checkout"); };

  return (
    <div className="fixed inset-0 font-sans" style={{ background: "linear-gradient(135deg, #F9FAFA 0%, #E6F2F2 100%)" }}>
      <div className="absolute left-6 top-6 z-[3500]">
        <button
          onClick={() => navigate("/checkout")}
          className="inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-stroke bg-white px-4 py-2.5 text-small font-semibold text-sub font-sans"
          style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
        >
          {"\u2039 " + t("checkout.verify.backToAccount")}
        </button>
      </div>

      <NovalaVerifyModal
        phone={phone}
        email={email}
        defaultMethod="email"
        availableMethods={["email", "text"]}
        onCodeSubmit={handleCodeSubmit}
        onVerified={handleVerified}
        onResend={handleResend}
        onClose={handleClose}
      />

      {sendError && (
        <div className="fixed left-1/2 top-6 z-[4000] -translate-x-1/2 rounded-md border p-3 text-small" style={{ background: "#FCE9E7", borderColor: "#F2C1BC", color: "#D9453C" }}>
          {sendError}
        </div>
      )}
    </div>
  );
}
